import { ReactElement, useMemo, useCallback } from 'react';
import { Stack, Typography, Box, Avatar } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faVectorSquare as LogisticsZonesIcon,
  faDollarSign as PriceIcon,
} from '@fortawesome/free-solid-svg-icons';
import { compact } from 'lodash';

import { OrderActiveFilters } from '~components/index';
import { OrderActiveFiltersItem } from '~components/OrderActiveFilters/types';
import { Nullable } from '~globals/types/commons';
import { formatterDate, formatCurrencyValue } from '~utils/formatter';
import { toAbsoluteUrl } from '~utils/assetsHelpers';
import {
  parseCapacityValueWithSuffix,
  getDescriptionCapacity,
} from '~utils/capacityTypes';
import { getDefaultAvatar } from '~utils/user';
import { useCurrentCapacityTypeInfo } from '~hooks/useCurrentCapacityTypeInfo';

// eslint-disable-next-line max-len
import { useDashboardVariantV2CarrierInWarehouseFiltersContext } from '../../../../../InWarehouseFiltersContext';
import { useDashboardVariantV2CarrierPresetsContext } from '../../../../../PresetsContext';
import { useDashboardVariantV2CarrierContentInWarehouseTabContext } from '../../Context';

import { DashboardVariantV2CarrierContentActionsContainer } from '../../../../styles';

const DashboardVariantV2CarrierContentInWarehouseTabCurrenFilters =
  (): Nullable<ReactElement> => {
    const { unit1CapacityTypeId, unit2CapacityTypeId } =
      useCurrentCapacityTypeInfo();

    const {
      autocompletesRefs: {
        services: servicesAutocompleteRef,
        logisticsZones: logisticsZonesAutocompleteRef,
        assignedUser: assignedUserAutocompleteRef,
        sourceImports: sourceImportsAutocompleteRef,
        pickingState: pickingStateAutocompleteRef,
        // itemState: itemStateAutocompleteRef,
      },
      filters: {
        filtersActives,
        handleClearFilters,
        handleClearFilterByKey,
        handleClearFilterByValues,
      },
    } = useDashboardVariantV2CarrierInWarehouseFiltersContext();

    const {
      selectedPreset: { reset: handleResetSelectedPreset },
    } = useDashboardVariantV2CarrierPresetsContext();

    const {
      actions: { handleForceRequests },
    } = useDashboardVariantV2CarrierContentInWarehouseTabContext();

    const onClearFilter = useCallback(() => {
      handleClearFilters(true);
      handleResetSelectedPreset();
    }, [handleClearFilters, handleResetSelectedPreset]);

    const activeFilters = useMemo<OrderActiveFiltersItem[]>(() => {
      const currentFilters: OrderActiveFiltersItem[] = [];

      if (filtersActives.search) {
        currentFilters.push({
          id: 'search',
          label: filtersActives.search,
          tooltip: 'Nro. Orden / Nro. Tracking / Grupo',
          onClear: () =>
            handleClearFilterByKey('search', '', handleForceRequests),
        });
      }

      const units1 = compact([filtersActives.unit1Gt, filtersActives.unit1Lt]);

      if (units1.length > 0) {
        const units1Label = units1
          .map((unit1) =>
            parseCapacityValueWithSuffix(unit1, unit1CapacityTypeId),
          )
          .join(' - ');

        currentFilters.push({
          id: 'units1',
          label: (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Box
                component="img"
                src={toAbsoluteUrl('/icons/icon_capacity_1.svg')}
                alt="Capacity One Icon"
                style={{ width: 15, height: 15 }}
              />

              <Typography variant="inherit">{units1Label}</Typography>
            </Stack>
          ),
          tooltip: getDescriptionCapacity(unit1CapacityTypeId),
          onClear: () =>
            handleClearFilterByValues(
              { unit1Gt: '', unit1Lt: '' },
              handleForceRequests,
            ),
        });
      }

      const units2 = compact([filtersActives.unit2Gt, filtersActives.unit2Lt]);

      if (units2.length > 0) {
        const units2Label = units2
          .map((unit2) =>
            parseCapacityValueWithSuffix(unit2, unit2CapacityTypeId),
          )
          .join(' - ');

        currentFilters.push({
          id: 'units2',
          label: (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Box
                component="img"
                src={toAbsoluteUrl('/icons/icon_capacity_2.svg')}
                alt="Capacity Two Icon"
                style={{ width: 15, height: 15 }}
              />

              <Typography variant="inherit">{units2Label}</Typography>
            </Stack>
          ),
          tooltip: getDescriptionCapacity(unit2CapacityTypeId),
          onClear: () =>
            handleClearFilterByValues(
              { unit2Gt: '', unit2Lt: '' },
              handleForceRequests,
            ),
        });
      }

      const price = compact([filtersActives.priceGt, filtersActives.priceLt]);

      if (price.length > 0) {
        const priceLabel = price
          .map((currentPrice) =>
            formatCurrencyValue(Number(currentPrice), { style: 'decimal' }),
          )
          .join(' - ');

        currentFilters.push({
          id: 'price',
          label: (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <FontAwesomeIcon
                icon={PriceIcon}
                style={{ width: 15, height: 15 }}
              />

              <Typography variant="inherit">{priceLabel}</Typography>
            </Stack>
          ),
          tooltip: 'Precio',
          onClear: () =>
            handleClearFilterByValues(
              { priceGt: '', priceLt: '' },
              handleForceRequests,
            ),
        });
      }

      const rangeExpirationDates = compact([
        filtersActives.expirationDateTimeFrom,
        filtersActives.expirationDateTimeTo,
      ]);

      if (rangeExpirationDates.length > 0) {
        const rangeExpirationDatesLabel = rangeExpirationDates
          .map((date) =>
            formatterDate(date, {
              format: 'DD/MM/YYYY',
            }),
          )
          .join(' - ');

        currentFilters.push({
          id: 'rangeExpirationDates',
          label: rangeExpirationDatesLabel,
          tooltip: 'Rango de fechas de vencimiento',
          onClear: () =>
            handleClearFilterByValues(
              { expirationDateTimeFrom: null, expirationDateTimeTo: null },
              handleForceRequests,
            ),
        });
      }

      if (filtersActives.includeWithoutExpirationDate) {
        currentFilters.push({
          id: 'includeWithoutExpirationDate',
          label: 'Sin fecha de vencimiento',
          tooltip: 'Incluir pedidos sin fecha de vencimiento',
          onClear: () =>
            handleClearFilterByKey(
              'includeWithoutExpirationDate',
              null,
              handleForceRequests,
            ),
        });
      }

      // if (
      //   filtersActives.itemState !== null &&
      //   filtersActives.itemState.length > 0
      // ) {
      //   const itemStateLabels = filtersActives.itemState
      //     .map((service) => service.label.toString())
      //     .join(' / ');

      //   currentFilters.push({
      //     id: 'itemState',
      //     label: itemStateLabels,
      //     tooltip: 'Estados',
      //     onClear: () => {
      //       itemStateAutocompleteRef.current?.resetAutocomplete();
      //       handleClearFilterByKey('itemState', null, handleForceRequests);
      //     },
      //   });
      // }

      if (filtersActives.pickingState) {
        currentFilters.push({
          id: 'pickingState',
          label: filtersActives.pickingState.label.toString(),
          tooltip: 'Estado de picking',
          onClear: () => {
            pickingStateAutocompleteRef.current?.resetAutocomplete();
            handleClearFilterByKey('pickingState', null, handleForceRequests);
          },
        });
      }

      if (
        filtersActives.services !== null &&
        filtersActives.services.length > 0
      ) {
        const servicesLabel = filtersActives.services
          .map((service) => service.label.toString())
          .join(' / ');

        currentFilters.push({
          id: 'services',
          label: servicesLabel,
          tooltip: 'Tipos de servicios',
          onClear: () => {
            servicesAutocompleteRef.current?.resetAutocomplete();
            handleClearFilterByKey('services', null, handleForceRequests);
          },
        });
      }

      if (filtersActives.zones !== null && filtersActives.zones.length > 0) {
        const zonesCount = filtersActives.zones.length - 1;

        const zonesLabel = filtersActives.zones.map((zone, zoneIndex) => (
          <Stack
            key={`filters-active-zone-${zone.zoneId}`}
            direction="row"
            spacing={0.5}
            alignItems="center"
          >
            {zone.color && (
              <FontAwesomeIcon
                icon={LogisticsZonesIcon}
                style={{ color: zone.color, width: 15, height: 15 }}
              />
            )}

            <Typography variant="inherit">{zone.code}</Typography>

            {zoneIndex < zonesCount && (
              <Typography variant="inherit">/</Typography>
            )}
          </Stack>
        ));

        currentFilters.push({
          id: 'zones',
          label: zonesLabel,
          tooltip: 'Zonas logísticas',
          onClear: () => {
            logisticsZonesAutocompleteRef.current?.resetAutocomplete();
            handleClearFilterByKey('zones', null, handleForceRequests);
          },
        });
      }

      if (filtersActives.assignedUser) {
        // eslint-disable-next-line max-len
        const assignedUserName = `${filtersActives.assignedUser.lastName}, ${filtersActives.assignedUser.firstName}`;

        currentFilters.push({
          id: 'assignedUser',
          label: (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Avatar
                src={getDefaultAvatar(
                  filtersActives.assignedUser.avatarUrl || undefined,
                )}
                alt={assignedUserName}
                sx={{ width: 15, height: 15 }}
              />

              <Typography variant="inherit">{assignedUserName}</Typography>
            </Stack>
          ),
          tooltip: 'Usuario asignado',
          onClear: () => {
            assignedUserAutocompleteRef.current?.resetAutocomplete();
            handleClearFilterByKey('assignedUser', null, handleForceRequests);
          },
        });
      }

      if (
        filtersActives.sourceImports !== null &&
        filtersActives.sourceImports.length > 0
      ) {
        const sourceImportsCount = filtersActives.sourceImports.length - 1;

        const sourceImportsLabel = filtersActives.sourceImports.map(
          (sourceImport, sourceImportIndex) => (
            <Stack
              key={`filters-active-source-import-${sourceImport.value}`}
              direction="row"
              spacing={0.5}
              alignItems="center"
            >
              <Box
                component="img"
                src={sourceImport.image.url}
                alt={sourceImport.image.alt}
                sx={{ height: 15, width: 15, objectFit: 'contain' }}
              />

              <Typography variant="inherit">{sourceImport.label}</Typography>

              {sourceImportIndex < sourceImportsCount && (
                <Typography variant="inherit">/</Typography>
              )}
            </Stack>
          ),
        );

        currentFilters.push({
          id: 'sourceImports',
          label: sourceImportsLabel,
          tooltip: 'Medios de importación',
          onClear: () => {
            sourceImportsAutocompleteRef.current?.resetAutocomplete();
            handleClearFilterByKey('sourceImports', null, handleForceRequests);
          },
        });
      }

      return currentFilters;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      filtersActives,
      handleClearFilterByKey,
      handleClearFilterByValues,
      handleForceRequests,
      unit1CapacityTypeId,
      unit2CapacityTypeId,
    ]);

    if (activeFilters.length === 0) return null;

    return (
      <DashboardVariantV2CarrierContentActionsContainer>
        <OrderActiveFilters
          filters={activeFilters}
          onClearFilters={onClearFilter}
        />
      </DashboardVariantV2CarrierContentActionsContainer>
    );
  };

export default DashboardVariantV2CarrierContentInWarehouseTabCurrenFilters;
